import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Plus, Trash2, Coins } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const SUPPORTED = ["GBP", "USD", "NGN", "EUR"];

/**
 * FxRatesPanel — Phase 5
 * Manage manual FX rates per workspace. Admin-only writes (server enforced).
 */
export default function FxRatesPanel({ workspaceId }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ from_currency: "USD", to_currency: "GBP", rate: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/fx-rates`, { credentials: "include" });
      if (r.ok) setRates(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const upsert = async () => {
    if (!draft.rate || draft.from_currency === draft.to_currency) {
      return toast.error("Choose distinct currencies and a rate");
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/fx-rates/${draft.from_currency}/${draft.to_currency}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_currency: draft.from_currency,
          to_currency: draft.to_currency,
          rate: parseFloat(draft.rate),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${r.status})`);
      }
      toast.success("FX rate saved");
      setDraft({ ...draft, rate: "" });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!confirm(`Delete ${r.from_currency}→${r.to_currency}?`)) return;
    try {
      const res = await fetch(`${API}/v2/workspaces/${workspaceId}/fx-rates/${r.from_currency}/${r.to_currency}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error("Delete failed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="fx-rates-panel">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800"><Coins className="h-5 w-5 text-amber-600" /> FX rates (manual)</h2>
        <p className="text-xs text-slate-500 mt-1">Used by aggregate views to convert into the header currency. Admin-only.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              {rates.length === 0 ? (
                <p className="text-xs text-slate-400">No FX rates configured. Add at least one pair to enable conversion.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase text-slate-500"><th className="text-left">From</th><th className="text-left">To</th><th className="text-right">Rate</th><th className="text-right">Updated</th><th></th></tr></thead>
                  <tbody>
                    {rates.map((r) => (
                      <tr key={r.fx_id} className="border-t border-slate-100" data-testid={`fx-row-${r.from_currency}-${r.to_currency}`}>
                        <td className="py-1 font-medium">{r.from_currency}</td>
                        <td className="py-1 font-medium">{r.to_currency}</td>
                        <td className="py-1 text-right tabular-nums">{r.rate}</td>
                        <td className="py-1 text-right text-xs text-slate-500">{r.updated_by} · {r.updated_at?.slice(0, 10)}</td>
                        <td className="py-1 text-right">
                          <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-red-600" data-testid={`fx-remove-${r.from_currency}-${r.to_currency}`}><Trash2 className="h-3 w-3" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="border-t border-slate-100 pt-3">
                <Label className="text-xs text-slate-600 mb-1 block">Add or update a pair</Label>
                <div className="flex flex-wrap gap-2 items-end">
                  <div>
                    <Label className="text-[11px] text-slate-500">From</Label>
                    <select value={draft.from_currency} onChange={(e) => setDraft({ ...draft, from_currency: e.target.value })} className="h-9 border border-slate-300 rounded-md px-2 text-sm" data-testid="fx-from-select">
                      {SUPPORTED.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">To</Label>
                    <select value={draft.to_currency} onChange={(e) => setDraft({ ...draft, to_currency: e.target.value })} className="h-9 border border-slate-300 rounded-md px-2 text-sm" data-testid="fx-to-select">
                      {SUPPORTED.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Rate (1 from = X to)</Label>
                    <Input type="number" step="0.0000001" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} className="w-40" data-testid="fx-rate-input" />
                  </div>
                  <Button size="sm" onClick={upsert} disabled={saving} data-testid="fx-save-btn">
                    {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    <Plus className="h-3 w-3 mr-1" /> Save
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
